import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Form, 
  Button, 
  Card, 
  Row, 
  Col, 
  Badge, 
  Spinner, 
  ProgressBar,
  Alert
} from 'react-bootstrap';
import { 
  IconPhoto, 
  IconUpload, 
  IconTrash, 
  IconX 
} from '@tabler/icons-react';
import MarketplaceServices from '../services/MarketplaceServices';

const GalleryManager = ({ 
  label = "Galeria de Imagens",
  value = [], 
  onChange, 
  error = null, 
  disabled = false,
  required = false,
  maxImages = 10,
  maxSizeMB = 3 // Máximo 3MB conforme API
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const maxFileSize = maxSizeMB * 1024 * 1024; // Convert to bytes
  // Tipos aceitos conforme documentação da API: PNG, JPG, JPEG, WEBP
  const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];

  // Validate image file
  const validateImageFile = useCallback((file) => {
    const errors = [];
    
    if (file.size > maxFileSize) {
      errors.push(`Arquivo muito grande (máximo ${maxSizeMB}MB)`);
    }
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('Tipo de arquivo não suportado (apenas PNG, JPG, JPEG, WEBP conforme API)');
    }
    
    return errors;
  }, [maxFileSize, maxSizeMB]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Check if adding these files would exceed the limit
    if (value.length + files.length > maxImages) {
      setUploadError(`Você pode ter no máximo ${maxImages} imagens. Atualmente você tem ${value.length} imagens.`);
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    const validFiles = [];
    const invalidFiles = [];

    // Validate all files
    files.forEach(file => {
      const errors = validateImageFile(file);
      if (errors.length === 0) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ file, errors });
      }
    });

    // Show validation errors
    if (invalidFiles.length > 0) {
      const errorList = invalidFiles.map(({ file, errors }) => 
        `${file.name}: ${errors.join(', ')}`
      ).join('\n');
      setUploadError(`Arquivos com problemas:\n${errorList}`);
    }

    if (validFiles.length === 0) {
      setUploading(false);
      return;
    }

    try {
      const processedImages = [];
      
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadProgress(((i + 1) / validFiles.length) * 100);

        // Create preview URL for display
        const previewUrl = URL.createObjectURL(file);

        processedImages.push({
          id: `temp_${Date.now()}_${i}`, // Temporary ID
          name: file.name,
          url: previewUrl,
          size: file.size,
          type: file.type,
          file: file, // Keep original file reference
          isNew: true // Mark as new upload
        });
      }

      // Add new images to existing ones
      const updatedImages = [...value, ...processedImages];
      onChange(updatedImages);

    } catch (error) {
      console.error('Erro no processamento:', error);
      setUploadError('Erro ao processar as imagens. Tente novamente.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove image
  const handleRemoveImage = (imageId) => {
    const updatedImages = value.filter(img => img.id !== imageId);
    onChange(updatedImages);
  };

  // Trigger file selection
  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const canAddMore = value.length < maxImages;
  const remainingSlots = maxImages - value.length;

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {label} {required && '*'}
        <Badge bg="secondary" className="ms-2">
          {value.length}/{maxImages}
        </Badge>
      </Form.Label>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || uploading || !canAddMore}
      />

      {/* Upload area */}
      {canAddMore && (
        <Card className="mb-3">
          <Card.Body className="text-center py-3">
            <IconUpload size={32} className="text-muted mb-2" />
            <div className="mb-2">
              <Button
                variant="primary"
                onClick={triggerFileSelect}
                disabled={disabled || uploading}
                size="sm"
              >
                <IconUpload className="me-1" size={16} />
                {uploading ? 'Processando...' : 'Adicionar Imagens'}
              </Button>
            </div>
            <div className="small text-muted">
              Máximo {maxSizeMB}MB por imagem • {remainingSlots} vagas restantes
              <br />
              Formatos: PNG, JPG, JPEG, WEBP
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Upload progress */}
      {uploading && (
        <Card className="mb-3">
          <Card.Body>
            <ProgressBar 
              now={uploadProgress} 
              label={`${Math.round(uploadProgress)}%`}
              className="mb-2"
              style={{ height: '6px' }}
            />
            <div className="text-center small text-muted">
              <Spinner size="sm" className="me-2" />
              Processando e redimensionando imagens...
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Error display */}
      {uploadError && (
        <Alert variant="danger" dismissible onClose={() => setUploadError(null)}>
          <div style={{ whiteSpace: 'pre-line' }}>{uploadError}</div>
        </Alert>
      )}

      {/* Validation error */}
      {error && (
        <div className="invalid-feedback d-block">
          {error}
        </div>
      )}

      {/* Images grid */}
      {Array.isArray(value) && value.length > 0 && (
        <Card>
          <Card.Header className="bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <IconPhoto className="me-1" size={16} />
                Imagens Selecionadas
              </h6>
              <Badge bg="primary">{value.filter(image => image && image.id).length} imagens</Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              {value.filter(image => image && image.id).map((image, index) => (
                <Col key={image.id} md={6} lg={4} xl={3}>
                  <Card className="h-100">
                    <div style={{ height: '150px', overflow: 'hidden', position: 'relative' }}>
                      <Card.Img
                        variant="top"
                        src={image.url && typeof image.url === 'string' && (image.url.startsWith('blob:') || image.url.startsWith('data:')) ? image.url : image.url ? `https://${image.url}` : ''}
                        style={{
                          height: '100%',
                          width: '100%',
                          objectFit: 'cover'
                        }}
                        alt={image.name || 'Imagem da galeria'}
                      />
                      
                      {/* Remove button */}
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        onClick={() => handleRemoveImage(image.id)}
                        disabled={disabled || uploading}
                        title="Remover imagem"
                      >
                        <IconX size={12} />
                      </Button>

                      {/* New image indicator */}
                      {image.isNew && (
                        <Badge 
                          bg="success" 
                          className="position-absolute top-0 start-0 m-1"
                        >
                          Nova
                        </Badge>
                      )}
                    </div>
                    
                    <Card.Body className="p-2">
                      <div className="small">
                        <div className="text-truncate fw-bold" title={image.name}>
                          {image.name}
                        </div>
                        <div className="text-muted">
                          {formatFileSize(image.size || 0)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Empty state */}
      {value.length === 0 && (
        <Card className="text-center py-4">
          <Card.Body>
            <IconPhoto size={48} className="text-muted mb-3" />
            <h6>Nenhuma imagem selecionada</h6>
            <p className="text-muted mb-3">
              Adicione até {maxImages} imagens para a galeria da publicação
            </p>
            <Button
              variant="outline-primary"
              onClick={triggerFileSelect}
              disabled={disabled || uploading}
            >
              <IconUpload className="me-1" size={16} />
              Selecionar Imagens
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Help text */}
      <Form.Text className="text-muted">
        <i className="las la-info-circle me-1" />
        Adicione até {maxImages} imagens de no máximo {maxSizeMB}MB cada. 
        As imagens serão redimensionadas automaticamente para 800x600px.
      </Form.Text>
    </Form.Group>
  );
};

export default GalleryManager;